import { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { BaseModal } from 'wbs-components-lib';

import { RewardModel } from '@shared/types';
import { getChangedRewardsInfo } from '@shared/utils';

import {
  useBulkCreateRewardsMutation,
  useBulkDeleteRewardsMutation,
  useBulkUpdateRewardsMutation,
  useLazyGetRewardsByIdsQuery,
  useUpdateVipLevelRewardMutation,
} from '@services';

import { AppDispatch, updateVipLevelRewards } from '@store';

import { VipLevelRewardForm, VipLevelRewardFormValues } from '../../VipSettingsForms';

import { VipLevelRewardEditModalProps } from './types';
import { getLevelFormValuesFromModel } from './utils';

export const VipLevelRewardEditModal: FC<VipLevelRewardEditModalProps> = ({
  isOpened,
  handleClose,
  vipLevelReward,
  vipLevelId,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [initialLevelRewards, setInitialLevelRewards] = useState<RewardModel[]>([]);
  const [isLevelProcessing, setIsLevelProcessing] = useState<boolean>(false);

  const [updateCurrentItem] = useUpdateVipLevelRewardMutation();
  const [getRewardsByIds] = useLazyGetRewardsByIdsQuery();
  const [bulkCreateRewards] = useBulkCreateRewardsMutation();
  const [bulkDeleteRewards] = useBulkDeleteRewardsMutation();
  const [bulkUpdateRewards] = useBulkUpdateRewardsMutation();

  useEffect(() => {
    if (isOpened) {
      const { rewardIds } = vipLevelReward;

      const getInitialRewards = async () => {
        const rewards = await getRewardsByIds(rewardIds ?? []).unwrap();

        setInitialLevelRewards(rewards);
      };

      if (rewardIds?.length) {
        getInitialRewards();
      } else {
        setInitialLevelRewards([]);
      }
    }
  }, [isOpened, vipLevelReward]);

  const handleUpdateItem = async ({ levelRewards, ...values }: VipLevelRewardFormValues) => {
    setIsLevelProcessing(true);
    try {
      const { deletedRewardIds, createdRewardItems, updatedRewardItems, restRewardItems } = getChangedRewardsInfo(
        initialLevelRewards,
        levelRewards
      );

      const isDeletedRewardsExisting = deletedRewardIds.length !== 0;

      if (isDeletedRewardsExisting) {
        await bulkDeleteRewards(deletedRewardIds);
      }

      let createdRewardIds: string[] = [];

      const isCreatedRewardsExisting = createdRewardItems.length !== 0;

      if (isCreatedRewardsExisting) {
        createdRewardIds = await bulkCreateRewards(createdRewardItems).unwrap();
      }

      const restLevelRewardItemIds = restRewardItems.map((item) => item.id);

      const isUpdatedRewardsExisting = updatedRewardItems.length !== 0;

      if (isUpdatedRewardsExisting) {
        await bulkUpdateRewards(updatedRewardItems).unwrap();
      }

      const updatedLevel = await updateCurrentItem({
        vipLevelRewardId: vipLevelReward.id,
        payload: {
          ...values,
          vipLevelId,
          rewardIds: [...createdRewardIds, ...restLevelRewardItemIds],
          order: vipLevelReward.order,
        },
      }).unwrap();

      dispatch(updateVipLevelRewards(updatedLevel));

      handleClose();
    } catch (error) {
      setIsLevelProcessing(false);
    } finally {
      setIsLevelProcessing(false);
    }
  };

  const initialValues = getLevelFormValuesFromModel(vipLevelReward, initialLevelRewards);

  return (
    <BaseModal headerTitle="Edit VIP task item" isOpened={isOpened} handleClose={handleClose} isFooterNeeded={false}>
      <VipLevelRewardForm
        submitButtonCaption="Edit"
        initialValues={initialValues}
        isSubmitting={isLevelProcessing}
        onSubmit={handleUpdateItem}
      />
    </BaseModal>
  );
};
